import { __esDecorate, __runInitializers } from "tslib";
import { asType, getType } from '#library/type.library.js';
import { isNumeric, isFunction } from '#library/assertion.library.js';
import { ownEntries, hasKey } from '#library/primitive.library.js';
import { secure, proxify } from '#library/proxy.library.js';
import { Serializable, StringTag } from '#library/decorator.library.js';
import { memoizeMethod } from '#library/function.library.js';
/** key to use for identifying Enumify objects */
const tag = 'Enumify';
const ENUM = secure(Object.create(null, {
    keys: memoizeMethod('keys', function () { return ownEntries(this, true).map(([key]) => key); }),
    values: memoizeMethod('values', function () { return ownEntries(this, true).map(([_, val]) => val); }),
    entries: memoizeMethod('entries', function () { return ownEntries(this, true).map(([key, val]) => Object.freeze([key, val])); }),
    invert: memoizeMethod('invert', function () { return Object.fromEntries(ENUM.entries.call(this).map(([key, val]) => [val, key])); }),
    has: value(function (key) { return ENUM.keys.call(this).includes(key); }),
    hasOwn: value(function (key) { return Object.prototype.hasOwnProperty.call(this, key); }),
    get: value(function (key) { return ENUM.has.call(this, key) ? this[key] : undefined; }),
    count: value(function () { return ENUM.keys.call(this).length; }),
    includes: value(function (search) { return ENUM.values.call(this).includes(search); }),
    keyOf: value(function (search) { return ENUM.invert.call(this)[search]; }),
    extend: value(function (list, frozen) { return enumify.call(this, list, frozen); }),
    forEach: value(function (fn, thisArg) {
        ENUM.entries.call(this).forEach((entry, index) => fn.call(thisArg, entry, index, this));
    }),
    filter: value(function (fn, thisArg) {
        return enumify(ENUM.entries.call(this).reduce((acc, entry, index) => (fn.call(thisArg, entry, index, this) ? Object.assign(acc, { [entry[0]]: entry[1] }) : acc), {}));
    }),
    map: value(function (fn, thisArg) {
        return enumify(ENUM.entries.call(this).reduce((acc, entry, index) => Object.assign(acc, { [entry[0]]: fn.call(thisArg, entry, index, this) }), {}));
    }),
    [Symbol.iterator]: value(function* () { for (const entry of ENUM.entries.call(this))
        yield entry; }),
    [Symbol.toStringTag]: { enumerable: false, configurable: false, writable: false, value: tag }
}));
function value(val) {
    return { enumerable: false, configurable: false, writable: false, value: val };
}
/**
 * # Enum
 * Static reflection and helper suite for Enumify objects.
 * Immune to member property shadowing and recommended over legacy prototype methods.
 */
const enumMethods = {
    /** Check if a key exists in an Enum or any inherited parent Enum */
    has(enumObj, key) {
        return ENUM.has.call(enumObj, key);
    },
    /** Check if a key exists directly as an own property on an Enum (mirrors Object.hasOwn) */
    hasOwn(enumObj, key) {
        return Object.prototype.hasOwnProperty.call(enumObj, key);
    },
    /** Safely retrieve a member value by key, returning undefined if absent */
    get(enumObj, key) {
        return ENUM.get.call(enumObj, key);
    },
    /** Safely extract all enum member keys, immune to shadowing */
    keys(enumObj) {
        return ENUM.keys.call(enumObj);
    },
    /** Safely extract all enum member values, immune to shadowing */
    values(enumObj) {
        return ENUM.values.call(enumObj);
    },
    /** Safely extract [key, value] pairs, immune to shadowing */
    entries(enumObj) {
        return ENUM.entries.call(enumObj);
    },
    /** Safely look up the key corresponding to a given value (reverse mapping) */
    keyOf(enumObj, search) {
        return ENUM.keyOf.call(enumObj, search);
    },
    /** Safely check if a value exists in an enum */
    includes(enumObj, search) {
        return ENUM.includes.call(enumObj, search);
    },
    /** Safely count total entries in an enum */
    count(enumObj) {
        return ENUM.count.call(enumObj);
    },
    /**
     * Safely invert an enum into a Value -> Key lookup dictionary.
     * Supports aliased enums (last-key-wins on duplicate values).
     */
    invert(enumObj) {
        return ENUM.invert.call(enumObj);
    },
    /** Extend an enum with new entries */
    extend(enumObj, list, frozen) {
        return enumify.call(enumObj, list, frozen);
    },
    /** Custom instance-of check supporting `val instanceof Enum` or predicate checking */
    [Symbol.hasInstance](instance) {
        return instance != null && (instance[Symbol.toStringTag] === 'Enumify' || isFunction(instance?.has));
    }
};
export const Enum = Object.freeze(Object.assign(Object.create(null), enumMethods));
export function enumify(list, frozen = true) {
    const type = getType(this);
    const proto = (type !== 'Module' && hasKey(this, 'has', isFunction)) ? this : ENUM;
    const arg = asType(list);
    const target = Object.create(proto);
    switch (arg.type) {
        case 'Enumify':
        case 'Object':
            Object.defineProperties(target, Object.getOwnPropertyDescriptors(arg.value));
            break;
        case 'Array':
            arg.value.forEach((key, index) => {
                if (isNumeric(key))
                    throw new TypeError(`Enumify: numeric keys are not supported ("${key}").`);
                if (Object.prototype.hasOwnProperty.call(target, key))
                    throw new TypeError(`Enumify: duplicate member key "${key}" found in array definition.`);
                Object.defineProperty(target, key, {
                    value: index,
                    enumerable: true,
                    writable: false,
                    configurable: false
                });
            });
            break;
        default:
            throw new TypeError(`Enumify: invalid argument type: ${arg.type}`);
    }
    return proxify(target, true, frozen); // proxy is ALWAYS frozen (read-only), but target is only 'locked' if requested
}
/** create an entry in the Serialization Registry to describe how to rebuild an Enum */
let Enumify = (() => {
    let _classDecorators = [Serializable, StringTag('Enumify')];
    let _classDescriptor;
    let _classExtraInitializers = [];
    let _classThis;
    var Enumify = class {
        static { _classThis = this; }
        static {
            const _metadata = typeof Symbol === "function" && Symbol.metadata ? Object.create(null) : void 0;
            __esDecorate(null, _classDescriptor = { value: _classThis }, _classDecorators, { kind: "class", name: _classThis.name, metadata: _metadata }, null, _classExtraInitializers);
            Enumify = _classThis = _classDescriptor.value;
            if (_metadata) Object.defineProperty(_classThis, Symbol.metadata, { enumerable: true, configurable: true, writable: true, value: _metadata });
            __runInitializers(_classThis, _classExtraInitializers);
        }
        constructor(list) {
            return enumify(list);
        }
    };
    return Enumify = _classThis;
})();
export { Enumify };
